import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/enviroment.prod';
import {
  Project,
  ProjectConfiguration,
  ProjectMode,
  ProjectSubmitResult,
} from 'src/app/models/project.model';
import { ConfigurationSnapshot } from 'src/app/models/configuration-snapshot.model';

export interface CreateConfigurationPayload {
  final_product_id: number;
  label: string;
  mp_code: string | null;
  mtrl: number | null;
  production_code?: string | null;
  room_label?: string | null;
  configuration: ConfigurationSnapshot;
  preview_image: string | null;
}

export interface SubmitProjectPayload {
  TRDR: string;
  country?: string | null;
  order_quantity?: number;
  quantities?: { [id: number]: number };
  /** Configuration ids to retry; omit to submit everything not yet accepted. */
  only?: number[];
}

/**
 * Thin HTTP layer for projects and cart items. All of these endpoints sit
 * behind auth:api — the bearer token is attached by AuthInterceptor.
 */
@Injectable({ providedIn: 'root' })
export class ProjectService {
  private readonly base = environment.apiLink;

  constructor(private http: HttpClient) { }

  /** Resumable projects (draft or partially submitted). */
  listResumable(): Observable<Project[]> {
    return this.http.get<{ data: Project[] }>(`${this.base}/projects`)
      .pipe(map(response => response.data));
  }

  create(mode: ProjectMode, name: string | null, productionCode: string | null, line: string | null): Observable<Project> {
    return this.http.post<{ data: Project }>(`${this.base}/projects`, {
      mode,
      name,
      production_code: productionCode,
      line,
    }).pipe(map(response => response.data));
  }

  /** The full resume payload: project plus its cart items, without snapshots. */
  get(projectId: number): Observable<Project> {
    return this.http.get<{ data: Project }>(`${this.base}/projects/${projectId}`)
      .pipe(map(response => response.data));
  }

  update(projectId: number, changes: Partial<Pick<Project, 'name' | 'production_code' | 'line' | 'order_quantity'>>): Observable<Project> {
    return this.http.put<{ data: Project }>(`${this.base}/projects/${projectId}`, changes)
      .pipe(map(response => response.data));
  }

  destroy(projectId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/projects/${projectId}`);
  }

  listConfigurations(projectId: number): Observable<ProjectConfiguration[]> {
    return this.http.get<{ data: ProjectConfiguration[] }>(`${this.base}/projects/${projectId}/configurations`)
      .pipe(map(response => response.data));
  }

  addConfiguration(projectId: number, payload: CreateConfigurationPayload): Observable<ProjectConfiguration> {
    return this.http.post<{ data: ProjectConfiguration }>(`${this.base}/projects/${projectId}/configurations`, payload)
      .pipe(map(response => response.data));
  }

  /** Only the preview image — the cart renders thumbnails without snapshots. */
  getConfigurationPreview(configurationId: number): Observable<string | null> {
    return this.http
      .get<{ data: { id: number; preview_image: string | null } }>(`${this.base}/project-configurations/${configurationId}/preview`)
      .pipe(map(response => response.data.preview_image));
  }

  /** The full row, including the snapshot and preview image. */
  getConfiguration(configurationId: number): Observable<ProjectConfiguration> {
    return this.http.get<{ data: ProjectConfiguration }>(`${this.base}/project-configurations/${configurationId}`)
      .pipe(map(response => response.data));
  }

  updateConfiguration(configurationId: number, payload: Partial<CreateConfigurationPayload>): Observable<ProjectConfiguration> {
    return this.http.put<{ data: ProjectConfiguration }>(`${this.base}/project-configurations/${configurationId}`, payload)
      .pipe(map(response => response.data));
  }

  deleteConfiguration(configurationId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/project-configurations/${configurationId}`);
  }

  reorder(projectId: number, ids: number[]): Observable<ProjectConfiguration[]> {
    return this.http.post<{ data: ProjectConfiguration[] }>(`${this.base}/projects/${projectId}/reorder`, { ids })
      .pipe(map(response => response.data));
  }

  /** One SALDOC per configuration, looped server-side. */
  submit(projectId: number, payload: SubmitProjectPayload): Observable<ProjectSubmitResult> {
    return this.http.post<{ data: ProjectSubmitResult }>(`${this.base}/projects/${projectId}/submit`, payload)
      .pipe(map(response => response.data));
  }
}
